/**
 * #1023: the visitor's marketing-consent decision, sent with the signup (`attribution.consent`) so the
 * backend can store it on the user (`users.marketingConsent`) and gate Meta CAPI events on it.
 * Pure: no DOM, no storage. No PII: enums, a location code, a timestamp and the consent-log id.
 */
import type { StoredConsent } from './contract';
import { isCurrentConsent, parseStoredConsent } from './contract';
import type { JurisdictionDecision } from './jurisdiction';
import { decideJurisdiction, parseGeoCookie } from './jurisdiction';

export type SignupConsentBasis = 'explicit' | 'default_opt_out_region';
export type SignupConsentSource = 'web' | 'extension';

/** Wire shape of `attribution.consent`; the backend validates every field again. */
export interface SignupConsent {
  marketing: boolean;
  /** explicit: a banner choice (or Global Privacy Control). default_opt_out_region: no choice, opt-out region. */
  basis: SignupConsentBasis;
  /** "US", "CA-QC", or "XX" when the location is unknown. */
  region: string;
  /** ISO time of the decision: the banner click, or now for a regional default. */
  at: string;
  source: SignupConsentSource;
  /** consent-log id of the banner choice. */
  consentId?: string;
}

export function regionCode(decision: JurisdictionDecision): string {
  return decision.region ? `${decision.country}-${decision.region}` : decision.country;
}

/**
 * The decision in effect for this visitor, or undefined when there is none to report: no banner choice
 * in an opt-in or unknown region means no consent and nothing to send (the backend fails closed).
 */
export function buildSignupConsent(input: {
  stored: StoredConsent | null;
  decision: JurisdictionDecision;
  source: SignupConsentSource;
  now: Date;
}): SignupConsent | undefined {
  const { stored, decision, source, now } = input;
  const region = regionCode(decision);
  if (isCurrentConsent(stored)) {
    return { marketing: stored.choices.marketing, basis: 'explicit', region, at: stored.ts, source, consentId: stored.consentId };
  }
  if (decision.jurisdiction !== 'opt_out') return undefined;
  // GPC is the visitor's own opt-out signal: report it as an explicit denial.
  if (decision.gpc) return { marketing: false, basis: 'explicit', region, at: now.toISOString(), source };
  return { marketing: true, basis: 'default_opt_out_region', region, at: now.toISOString(), source };
}

/**
 * Extension signups run on web.whatsapp.com, where blueticks.co cookies are not readable. The attribution
 * bridge copies `bt_consent` and `bt_geo` from blueticks.co into chrome.storage; this rebuilds the
 * decision from those copies.
 */
export function buildBridgedSignupConsent(input: {
  bridgedConsent: string | undefined;
  bridgedGeo: string | undefined;
  gpc: boolean;
  now: Date;
}): SignupConsent | undefined {
  const geo = parseGeoCookie(input.bridgedGeo);
  const decision = decideJurisdiction({ country: geo?.country ?? null, region: geo?.region ?? null, gpc: input.gpc });
  return buildSignupConsent({
    stored: parseStoredConsent(input.bridgedConsent),
    decision,
    source: 'extension',
    now: input.now,
  });
}
